import { useState } from 'react';
import {
  Box, Grid, InputLabel, TextField, Typography, Popover, Button, Select, MenuItem, Checkbox, FormControlLabel,
  Accordion, AccordionSummary, AccordionDetails, IconButton
} from '@mui/material';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Formik, Form, FieldArray } from 'formik';

interface CameraComponentProps {
  values: any;
  index: number;
  childIndex: number;
  setFieldValue: (key: string, value: any) => void;
  onSubmit: (data: any) => void;
}

export default function CameraComponent({ values, index, childIndex, setFieldValue }: CameraComponentProps) {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const id = open ? 'edit-camera-popover' : undefined;

  const child = values.fields[index].children[childIndex];
  const settings = child.settings || {};
  const label = child.label || child.question || 'Camera';

  // FIX: Define labelFields as an array
  const labelFields = [
    { label: 'Label', name: 'label', type: 'text' },
    { label: 'Min Duration', name: 'minDuration', type: 'number' }
  ];

  return (
    <Box>
      <Grid container alignItems="center" spacing={1}>
        <Grid item xs={11}>
          <InputLabel sx={{ color: 'black' }}>{label}</InputLabel>
        </Grid>
        <Grid item xs={1}>
          <Box sx={{ cursor: 'pointer', color: 'primary.main' }} onClick={handleOpen}>
            <CameraAltIcon fontSize="medium" />
          </Box>
        </Grid>
      </Grid>

      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        PaperProps={{ sx: { width: 350, p: 2 } }}
      >
        <Formik
          initialValues={{
            label: child.label || '',
            required: settings.required || false,
            imageInstructions: child.children && child.children.length > 0
              ? child.children
              : [{ title: '', description: '' }],
            cameraFacing: settings.cameraFacing || 'env',
            cameraMode: settings.cameraMode || 'IMAGE',
            minDuration: settings.minDuration || 0
          }}
          enableReinitialize
          onSubmit={(formValues) => {
            const updatedChild = {
              ...child,
              label: formValues.label,
              settings: {
                required: formValues.required,
                cameraFacing: formValues.cameraFacing,
                cameraMode: formValues.cameraMode,
                minDuration: formValues.minDuration
              },
              children: formValues.imageInstructions // <-- Save here!
            };
            setFieldValue(`fields.${index}.children.${childIndex}`, updatedChild);
            handleClose();
          }}
        >
          {({ values: formValues, handleChange, handleBlur }) => (
            <Form>
              <Typography variant="subtitle1" mb={2}>
                Edit Camera Settings
              </Typography>

              <Grid container spacing={2}>
                {/* FIX: Use labelFields array */}
                {labelFields.map(({ label, name, type }) => (
                  <Grid container item xs={12} key={name} alignItems="center" spacing={1}>
                    <Grid item xs={5}>
                      <InputLabel>{label}</InputLabel>
                    </Grid>
                    <Grid item xs={7}>
                      <TextField
                        fullWidth
                        name={name}
                        type={type}
                        value={formValues[name as keyof typeof formValues]}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        size="small"
                      />
                    </Grid>
                  </Grid>
                ))}

                {/* Camera Facing */}
                <Grid container item xs={12} alignItems="center" spacing={1}>
                  <Grid item xs={5}>
                    <InputLabel>Camera Facing</InputLabel>
                  </Grid>
                  <Grid item xs={7}>
                    <Select
                      fullWidth
                      name="cameraFacing"
                      value={formValues.cameraFacing}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      size="small"
                    >
                      <MenuItem value="env">Environment</MenuItem>
                      <MenuItem value="front">Front</MenuItem>
                    </Select>
                  </Grid>
                </Grid>

                {/* Camera Mode */}
                <Grid container item xs={12} alignItems="center" spacing={1}>
                  <Grid item xs={5}>
                    <InputLabel>Camera Mode</InputLabel>
                  </Grid>
                  <Grid item xs={7}>
                    <Select
                      fullWidth
                      name="cameraMode"
                      value={formValues.cameraMode}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      size="small"
                    >
                      <MenuItem value="IMAGE">IMAGE</MenuItem>
                      <MenuItem value="VIDEO">VIDEO</MenuItem>
                    </Select>
                  </Grid>
                </Grid>

                {/* Image Instructions Accordion */}
                {formValues.cameraMode === 'IMAGE' && (
                  <Grid item xs={12}>
                    <FieldArray name="imageInstructions">
                      {({ push, remove }) => (
                        <Box>
                          {formValues.imageInstructions.map((img: any, idx: number) => (
                            <Accordion key={idx} sx={{ mb: 1 }}>
                              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Typography fontWeight="bold">
                                  Image {idx + 1}
                                </Typography>
                              </AccordionSummary>
                              <AccordionDetails>
                                <Grid container spacing={2}>
                                  <Grid item xs={12}>
                                    <TextField
                                      fullWidth
                                      label="Title"
                                      name={`imageInstructions.${idx}.title`}
                                      value={img.title}
                                      onChange={handleChange}
                                      size="small"
                                    />
                                  </Grid>
                                  <Grid item xs={12}>
                                    <TextField
                                      fullWidth
                                      label="Description"
                                      name={`imageInstructions.${idx}.description`}
                                      value={img.description}
                                      onChange={handleChange}
                                      size="small"
                                    />
                                  </Grid>
                                  <Grid item xs={12}>
                                    <Box display="flex" justifyContent="flex-end">
                                      <IconButton
                                        onClick={() => remove(idx)}
                                        color="error"
                                        disabled={formValues.imageInstructions.length === 1}
                                      >
                                        <DeleteIcon />
                                      </IconButton>
                                    </Box>
                                  </Grid>
                                </Grid>
                              </AccordionDetails>
                            </Accordion>
                          ))}
                          <Box display="flex" justifyContent="flex-end" mt={1}>
                            <Button
                              variant="outlined"
                              startIcon={<AddIcon />}
                              onClick={() => push({ title: '', description: '' })}
                            >
                              Add Image Instruction
                            </Button>
                          </Box>
                        </Box>
                      )}
                    </FieldArray>
                  </Grid>
                )}

                {/* Required Checkbox */}
                <Grid item xs={12}>
                  <FormControlLabel
                    control={<Checkbox name="required" checked={formValues.required} onChange={handleChange} />}
                    label="Required"
                  />
                </Grid>

                {/* Save Button */}
                <Grid item xs={12}>
                  <Box display="flex" justifyContent="flex-end" mt={1}>
                    <Button type="submit" variant="contained" size="small">
                      Save
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </Form>
          )}
        </Formik>
      </Popover>
    </Box>
  );
}
